import { bbox } from "@turf/turf";
import {
  type DocumentData,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore/lite";
import { db } from "senf-shared";
import {
  delay,
  getAgeGroupName,
  getSentimentName,
  parseGeoData,
  todayIsWithinDateRange,
  topicsArrayToObject,
} from "../utils/helpers";
import { fetchGisDataFromStorage } from "../utils/mapHelpers";
import { getStartAndEndDate } from "../utils/projectHelpers";

export const setProjectMainData = async (projectId: string) => {
  const maxRetries = 10;

  try {
    const projectMainData = await fetchProjectMainData(projectId);
    return projectMainData;
  } catch (error) {
    console.log(
      `Error occurred while fetching project with projectId ${projectId}. Retrying...`,
    );
    console.log(error);

    for (let retries = 1; retries <= maxRetries; retries++) {
      try {
        const projectMainData = await fetchProjectMainData(projectId);
        return projectMainData;
      } catch (error) {
        console.log(`Retry ${retries}/${maxRetries} failed.`);
        console.log(error);
        await delay(1000); // Adjust the delay time as needed
      }
    }

    console.log(
      `Could not fetch project with projectId ${projectId} after ${maxRetries} retries.`,
    );
    return null;
  }
};

const fetchProjectMainData = async (projectId: string) => {
  const projectsRef = collectionGroup(db, "projects");
  const q = query(projectsRef, where("projectId", "==", projectId));
  const projectsQuerySnapshot = await getDocs(q);

  if (projectsQuerySnapshot.empty) {
    return null;
  }

  const data = {
    ...projectsQuerySnapshot.docs[0].data(),
    color: projectsQuerySnapshot.docs[0].data()?.color || "#ffd061",
  };

  return data;
};

export const setProjectIdeaMaps = async (
  projectMainData,
  registrations,
  maps,
  filtered,
) => {
  const ideaMapsRef = collection(
    db,
    `organizations/${projectMainData.organizationId}/projects/${projectMainData.projectId}/ideaMaps`,
  );
  const q3 = query(ideaMapsRef, orderBy("createdAt", "desc"));

  try {
    const ideaMapsQuerySnapshot = await getDocs(q3);

    if (ideaMapsQuerySnapshot.empty) {
      return [];
    }

    const ideaMapsPromises = ideaMapsQuerySnapshot.docs.map(async (doc) => {
      const ideaMapId = doc.id;
      const data = {
        ...doc.data(),
        ideaMapId,
        customTopicsObject: doc.data()?.customTopics?.length
          ? topicsArrayToObject(doc.data()?.customTopics)
          : null,
      };

      if (maps) {
        data.mapbounds = maps.find(
          (map) => map.mapId === data?.mapId,
        )?.mapbounds;
        data.mapStyle = maps.find((map) => map.mapId === data?.mapId)?.mapStyle;
        data.gisData = maps.find((map) => map.mapId === data?.mapId)?.gisData;
      }

      if (data?.mapbounds) {
        data.bbox = bbox(parseGeoData(data.mapbounds));
      }

      const ideas = await setProjectIdeas(
        projectMainData,
        data,
        registrations,
        filtered,
      );
      const likes = await setProjectLikes(projectMainData, data, registrations);
      const comments = await setProjectComments(
        projectMainData,
        data,
        registrations,
      );

      const ideaMapData = {
        ...data,
        ideas,
        likes,
        comments,
      };

      // Uncomment and adjust the below code if needed
      // if (
      //   filtered &&
      //   (!todayIsWithinDateRange(ideaMapData.startDate, ideaMapData.endDate) ||
      //     !ideaMapData?.settings?.publicAccess)
      // ) {
      //   return null;
      // }

      return ideaMapData;
    });

    const ideaMaps = await Promise.all(ideaMapsPromises);
    return ideaMaps.filter(Boolean);
  } catch (error) {
    console.error("Error fetching idea maps:", error);
    // Handle the error appropriately
    return [];
  }
};

export const setProjectIdeas = async (
  project,
  ideaMap,
  registrations,
  filtered,
) => {
  if (!project) throw new Error("Project is missing in setProjectIdeas");
  if (!project.organizationId)
    throw new Error("OrganizationId is missing in setProjectIdeas");
  if (!project.projectId)
    throw new Error("ProjectId is missing in setProjectIdeas");
  const ideasRef = collection(
    db,
    `organizations/${project.organizationId}/projects/${project.projectId}/ideas`,
  );

  // const ideaTranslationsQuery = query(
  //   collectionGroup(db, `ideaTranslations`),
  //   where("ideaMapId", "==", ideaMap.ideaMapId)
  // );
  // const ideaTranslationsData = await getDocs(ideaTranslationsQuery);
  // let translation = {}; // Initialize as an object

  // ideaTranslationsData.forEach((document) => {
  //   const data = document.data();
  //   const ideaId = data.ideaId; // Assuming 'ideaId' is a property of your document data
  //   translation[ideaId] = { ...data }; // Assign using ideaId as key
  // });

  const q2 = query(ideasRef, where("projectId", "==", project.projectId));

  const q3 = query(
    q2,
    where("ideaMapId", "==", ideaMap.ideaMapId),
    orderBy("createdAt", "desc"),
  );

  const ideasQuerySnapshot = await getDocs(q3);
  const ideas: DocumentData = [];
  if (ideasQuerySnapshot.empty) {
    return [];
  }

  ideasQuerySnapshot.forEach((doc) => {
    let { body } = doc.data();
    if (filtered) {
      if (doc.data().isHidden) return;
      body = body.substr(0, 200);
    }

    const ideaData = {
      ...doc.data(),
      // createdAt:
      //   dateFormatting === false
      //     ? doc.data().createdAt
      //     : formatUS(doc.data().createdAt),
      ideaId: doc.id,
      // title: translation?.[doc?.data()?.ideaId]?.title || doc.data().title,
      // body: translation?.[doc?.data()?.ideaId]?.body || doc.data().body,
      body,
      geoData:
        doc.data().geoDataType === "location" ||
        doc.data().geoDataType === "noLocation"
          ? null
          : {
              ...parseGeoData(doc.data().geoData),
              features: parseGeoData(doc.data().geoData)?.features.map(
                (feature) => ({
                  ...feature,
                  properties: {
                    ...feature.properties,
                    ideaId: doc.id,
                    title: doc.data().title,
                    color:
                      ideaMap?.customTopicsObject?.[doc.data().topic]?.color,
                  },
                }),
              ),
            },
      geoReference: doc.data().geoDataType === "noLocation" ? "Nein" : "Ja",
      topicLabel: ideaMap?.customTopicsObject?.[doc.data().topic]?.label,
      color: ideaMap?.customTopicsObject?.[doc.data().topic]?.color,
      ageGroup: getAgeGroupName(doc.data()?.age),
      circleRadius: 5 + doc.data()?.likeCount / 7,

      lang: doc.data()?.lang || project?.settings?.originalLanguage || "de",
      sentimentName: getSentimentName(doc.data()?.sentiment || 0),
      // ...doc?.data()?.tags?.reduce((acc, tag) => {
      //   if (tag.key) {
      //     // acc[tag.key] = tag.value;
      //     acc[tag.key] = project.ideaTableColumns
      //       .find((tagColumn) => tagColumn?.value === tag.key)
      //       ?.values?.find((tagValue) => tagValue?.value === tag.value)?.label;

      //     const tagKey = `${tag.key}-object`;
      //     acc[tagKey] = project.ideaTableColumns
      //       .find((tagColumn) => tagColumn?.value === tag.key)
      //       ?.values?.find((tagValue) => tagValue?.value === tag.value);
      //   }
      //   return acc;
      // }, {}),
    };

    if (
      registrations &&
      registrations.find((item) => item.userId === ideaData.userId)
    ) {
      const userData = registrations.find(
        (item) => item.userId === ideaData.userId,
      );
      ideaData.userData = userData;
    }
    ideas.push(ideaData);
  });

  return ideas;
};

// export const countProjectIdeas = async (projectId: string) => {
//   if (!projectId) {
//     console.error("No projectId provided in countProjectIdeas");
//     return null;
//   }
//   const ideasRef = collectionGroup(db, "ideas");
//   const q2 = query(ideasRef, where("projectId", "==", projectId));

//   const ideasQuerySnapshot = await getDocs(q2);
//   const ideasCount = ideasQuerySnapshot.size;
//   return ideasCount;
// };

export const setProjectLikes = async (projectMainData, data, registrations) => {
  const likesRef = collection(db, "likes");
  const q = query(
    likesRef,
    where("projectId", "==", projectMainData.projectId),
  );
  const q1 = query(q, where("ideaMapId", "==", data.ideaMapId));

  const querySnapshot = await getDocs(q1);
  const likes: DocumentData = [];
  querySnapshot.forEach((doc) => {
    const likeData = {
      ...doc.data(),
      topicLabel: data?.customTopicsObject?.[doc.data().topic]?.label,
    };
    if (
      registrations &&
      registrations.find((item) => item.userId === likeData.userId)
    ) {
      const userData = registrations.find(
        (item) => item.userId === likeData.userId,
      );
      likeData.userData = userData;
    }
    likes.push(likeData);
  });

  return likes;
};

export const setProjectComments = async (
  projectMainData,
  data,
  registrations,
) => {
  const commentsRef = collection(db, "comments");
  const q = query(
    commentsRef,
    where("projectId", "==", projectMainData.projectId),
  );
  const q1 = query(q, where("ideaMapId", "==", data.ideaMapId));

  const querySnapshot = await getDocs(q1);
  const comments: DocumentData = [];

  querySnapshot.forEach((doc) => {
    const commentData = {
      ...doc.data(),
      topicLabel: data?.customTopicsObject?.[doc.data().topic]?.label,
    };
    if (
      registrations &&
      registrations.find((item) => item.userId === commentData.userId)
    ) {
      const userData = registrations.find(
        (item) => item.userId === commentData.userId,
      );
      commentData.userData = userData;
    }
    comments.push(commentData);
  });

  return comments;
};

export const setProjectSurveys = async (
  organizationId,
  projectId,
  filtered,
) => {
  const surveysRef = collection(
    db,
    `organizations/${organizationId}/projects/${projectId}/surveys`,
  );
  const q3 = query(surveysRef, orderBy("createdAt", "desc"));

  const surveysQuerySnapshot = await getDocs(q3);

  if (surveysQuerySnapshot.empty) {
    return [];
  }

  const surveys = Array.from(surveysQuerySnapshot.docs, (doc) => {
    const surveyData = { ...doc.data(), documentId: doc.id };

    // Regular expression to check if surveyId ends with '-something'

    const endsWithPattern = /-(deSimpleLanguage|\w+)$/;
    const endsWithCondition = endsWithPattern.test(surveyData.documentId);

    if (
      endsWithCondition ||
      (filtered &&
        (!todayIsWithinDateRange(surveyData.startDate, surveyData.endDate) ||
          !surveyData?.settings?.publicAccess)) // Include the regular expression condition in the filtering logic
    ) {
      return null;
    }

    return surveyData;
  }).filter(Boolean);

  return surveys;
};

export const setProjectSurveyEntries = async (project, registrations) => {
  const entriesRef = collectionGroup(db, "entries");
  const q = query(entriesRef, where("projectId", "==", project.projectId));
  // const q1 = query(q, where("completed", "==", true));
  const querySnapshot = await getDocs(q);
  const surveyEntries = [];
  querySnapshot.forEach((doc) => {
    const entryData = doc.data();
    if (!entryData?.userId) {
      // Handle the case where userId is not even defined in survey
      return;
    }

    if (Object.keys(entryData.values || {}).length === 0) {
      // Handle the case where entryData is undefined or an empty object
      return;
    }

    entryData.ageGroup = getAgeGroupName(doc.data()?.age);
    entryData.userPostcode = doc.data()?.userPostcode;
    // || "noInformation";
    entryData.gender = doc.data()?.gender;
    entryData.lang =
      doc.data()?.lang || project?.settings?.originalLanguage || "de";

    // || "noInformation";

    if (entryData.surveyMode !== "test") {
      if (
        registrations &&
        registrations.find((item) => item.userId === entryData.userId)
      ) {
        const userData = registrations.find(
          (item) => item.userId === entryData.userId,
        );

        // console.log(userData);

        entryData.userData = userData;
      }

      // console.log(entryData);
      surveyEntries.push(entryData);
    }
  });

  return surveyEntries;
};

export const setProjectMaps = async (organizationId, projectId) => {
  const mapsRef = collection(
    db,
    `organizations/${organizationId}/projects/${projectId}/maps`,
  );
  const q3 = query(mapsRef, orderBy("createdAt", "desc"));

  const mapsQuerySnapshot = await getDocs(q3);

  if (mapsQuerySnapshot.empty) {
    return [];
  }

  const mapsPromises = mapsQuerySnapshot.docs.map(async (doc) => {
    const gisData = await fetchGisDataFromStorage(doc.data().gisData);

    const mapData = {
      ...doc.data(),
      mapbounds: JSON.parse(doc.data().mapbounds),
      gisData: gisData || [],
      createdAt: doc.data().createdAt,
      mapId: doc.id,
    };

    return mapData;
  });

  const maps = await Promise.all(mapsPromises);

  return maps;
};

export const setProjectRegistrations = async (projectMainData, surveys) => {
  const q = collection(
    db,
    `organizations/${projectMainData.organizationId}/projects/${projectMainData.projectId}/participants`,
  );

  const querySnapshot = await getDocs(q);
  const users: DocumentData = [];

  const ideaMapsRef = collection(
    db,
    `organizations/${projectMainData.organizationId}/projects/${projectMainData.projectId}/ideaMaps`,
  );
  const q3 = query(ideaMapsRef, orderBy("createdAt", "desc"));
  const ideaMapsQuerySnapshot = await getDocs(q3);

  const ideaMaps = Array.from(ideaMapsQuerySnapshot.docs, (doc) => {
    const data = { ...doc.data(), ideaMapId: doc.id };
    return data;
  }).filter(Boolean);

  const { firstStartDate } = getStartAndEndDate({ surveys, ideaMaps });


  querySnapshot.forEach((doc) => {
    if (doc.data()?.mode === "test") {
      return;
    }


    if (new Date(doc.data().createdAt) < new Date(firstStartDate)) {
      return;
    }

    users.push({
      ...doc.data(),
      age: doc.data().age ? new Date().getFullYear() - Number.parseInt(doc.data().age) : null,
      registrationDate: new Date(doc.data().createdAt).toLocaleString("en", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
    });

  });
  return users;
};

export const setProjectEngagedUsers = async (
  registrations,
  ideas,
  likes,
  comments,
  surveyEntries,
) => {
  // Convert each engagement dataset into a Set of user IDs for fast lookup
  const ideaUserIds = new Set(ideas.map((idea) => idea.userId));
  const likeUserIds = new Set(likes.map((like) => like.userId));
  const commentUserIds = new Set(comments.map((comment) => comment.userId));
  const surveyUserIds = new Set(surveyEntries.map((entry) => entry.userId));

  // Filter registrations to only those who have engaged in at least one dataset
  const engagedUsers = registrations.filter((registration) => {
    const { userId } = registration;
    // Check if the user's ID is in any of the engagement datasets
    return (
      ideaUserIds.has(userId) ||
      likeUserIds.has(userId) ||
      commentUserIds.has(userId) ||
      surveyUserIds.has(userId)
    );
  });


  return engagedUsers;
};

export const setProjectCampaigns = async (project) => {
  const campaignsRef = collection(
    db,
    `organizations/${project.organizationId}/projects/${project.projectId}/campaigns`,
  );
  const q3 = query(campaignsRef, orderBy("createdAt", "desc"));

  const campaigns: DocumentData = [];

  const campaignsQuerySnapshot = await getDocs(q3);
  if (campaignsQuerySnapshot.empty) {
  } else {
    campaignsQuerySnapshot.forEach((doc) => {
      campaigns.push({
        ...doc.data(),
      });
    });

    // campaigns.push({
    //   title: "Andere",
    //   utm_id: "none",
    //   createdAt: new Date().toISOString(),
    // });

    return campaigns;
  }
};

// export const setProjectUmamiData = async (project, type) => {
//   const dateToday = Date.now();

//   // Development
//   // const SITE_ID = "f5d737f5-ec96-45e8-843c-ffef8e669a55";

//   const SITE_ID =
//     import.meta.env.VITE_APP_MODE === "development" ||
//     import.meta.env.VITE_APP_MODE === "staging"
//       ? "51df5aa1-e967-429a-adf5-cc41f0a86a49"
//       : "f5d737f5-ec96-45e8-843c-ffef8e669a55";

//   const request = (type) => {
//     // return `${SITE_ID}/${type}?start_at=${startAt}&end_at=${dateToday}&unit=day&tz=Europe%2FBerlin&url=%2Fwismar%2Fprojects%2FKrxou9cPbJkWWvQU182y`;
//     return `${SITE_ID}/${type}?start_at=${new Date(
//       project.startDate
//     ).getTime()}&end_at=${dateToday}&unit=day&tz=Europe%2FBerlin&url=%2Fprojects%2F${
//       project.projectId
//     }/info`;
//   };

//   try {
//     const requestCall = request(type);
//     const response = await fetch(
//       `${import.meta.env.VITE_FIREBASE_CLOUD_FUNCTIONS_URL}/umamiStats`,
//       {
//         method: "POST",
//         headers: {
//           "Content-Type": "application/json",
//         },
//         body: JSON.stringify({ requestCall }),
//       }
//     );

//     const umamiData = await response.json();
//     console.log(umamiData);

//     return umamiData;
//   } catch (error) {
//     console.log("error", error);
//   }
// };
